import * as RadixTooltip from "@radix-ui/react-tooltip";
import { type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import { floatingCollisionPadding, floatingLayerClassName, tooltipContentCollisionClassName } from "../overlayPositioning";

export type TooltipProps = {
  content: ReactNode;
  children: ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  variant?: "default" | "white";
  delayDuration?: number;
  /** When true, the tooltip never opens but the trigger stays mounted unchanged. */
  disabled?: boolean;
};

const tooltipVariantClassName = {
  default: "bfrs-bg-text-primary bfrs-text-text-inverse",
  white: "bfrs-border bfrs-border-border bfrs-bg-white bfrs-text-text-primary"
} as const;

const tooltipArrowClassName = {
  default: "bfrs-fill-text-primary",
  white: "bfrs-fill-white"
} as const;

export function Tooltip({ content, children, side = "top", variant = "default", delayDuration = 350, disabled = false }: TooltipProps) {
  const resolvedVariant = variant === "white" ? "white" : "default";

  return (
    <RadixTooltip.Provider delayDuration={delayDuration}>
      <RadixTooltip.Root open={disabled ? false : undefined}>
        <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <BfrsPortalScope>
            <RadixTooltip.Content
              side={side}
              sideOffset={6}
              collisionPadding={floatingCollisionPadding}
              className={cn(
                floatingLayerClassName,
                "bfrs-rounded-md bfrs-px-3 bfrs-py-2 bfrs-text-xs bfrs-leading-5 bfrs-shadow-elevated bfrs-animate-content-in",
                tooltipVariantClassName[resolvedVariant],
                tooltipContentCollisionClassName
              )}
            >
              {content}
              <RadixTooltip.Arrow className={tooltipArrowClassName[resolvedVariant]} />
            </RadixTooltip.Content>
          </BfrsPortalScope>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}
